import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type AlarmHistoryRow } from '@/lib/api';
import { useAuthStore } from '@/store/auth-store';
import { useEventsStore, type AlarmEvent } from '@/store/events-store';
import { alarmName } from '@/lib/alarm-names';

// =============================================================================
// useAlarmHistory — initial load alarm history dari tracks_alarm, populate
// events store. Scoped by effectiveUserId (impersonation aware).
//
// Re-load setiap effective user berubah. WS event subsequent di-merge ke
// store via EventNotifier (dedupe by id).
// =============================================================================

function rowToEvent(r: AlarmHistoryRow, idx: number): AlarmEvent {
  // id HARUS unik (dipakai sebagai React key + key di store/checkbox). Backend
  // lama bisa kirim id=0 untuk semua baris → fallback komposit (vh+alarm+idx)
  // supaya tetap unik. dbId hanya di-set kalau id asli (>0) → tombol hapus
  // hanya aktif untuk baris yang benar-benar punya row id di DB.
  return {
    id: r.id ? `db-${r.id}` : `evt-${r.vh_id}-${r.alarm_code}-${idx}`,
    dbId: r.id || undefined,
    vehicleId: r.vh_id,
    vehicleName: r.nopol || `#${r.vh_id}`,
    imei: r.imei,
    alarmId: r.alarm_code,
    alarmName: alarmName(r.alarm_code),
    lat: r.lat,
    lng: r.lng,
    speed: r.speed,
    address: r.address,
    geofence: r.gf || undefined,
    timestamp: r.timestamp,
  };
}

export function useAlarmHistory(limit = 200) {
  const userId = useAuthStore((s) => s.effectiveUserId());
  const viewAsUserId = useAuthStore((s) => s.viewAsUserId);
  const setAll = useEventsStore((s) => s.setAll);

  const q = useQuery({
    queryKey: ['alarm-recent', userId, limit],
    queryFn: () => api.alarmRecent({
      limit,
      offset: 0,
      // Kirim id_user kalau impersonate; tanpa itu backend pakai callerID dari JWT.
      ...(viewAsUserId ? { id_user: viewAsUserId } : {}),
    }),
    enabled: !!userId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!q.data?.rows) return;
    setAll(q.data.rows.map(rowToEvent));
  }, [q.data, setAll]);

  return q;
}
